import React, { useState, useMemo, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "../UI/dialog"
import { Avatar, AvatarFallback } from "../UI/avatar"
import { ArrowLeft, Search, X, Settings } from 'lucide-react'
import { Input } from "../UI/input"
import { Button } from "../UI/button"

export function ContactList({
  contacts = [],
  triggerText = "View Contacts",
  onContactClick,
  externalOpen = false,
  onOpenChange,
  permissionDenied = false,
  onRequestPermission
}) {
  const [searchTerm, setSearchTerm] = useState("")

  // set searchTerm to empty when component re render
  useEffect(() => {
    if (externalOpen) {
      setSearchTerm("")
    }
  }, [externalOpen])

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const filteredContacts = useMemo(() => {
    const normalize = (str) => str?.toLowerCase().replace(/[^a-z0-9]/g, '') || ''
    const searchNormalized = normalize(searchTerm)

    return contacts
      .filter(contact =>
        normalize(contact.name).includes(searchNormalized) ||
        normalize(contact.phone).includes(searchNormalized) ||
        normalize(contact.email).includes(searchNormalized) ||
        normalize(contact.org).includes(searchNormalized)
      )
      .sort((a, b) => a.name.localeCompare(b.name)) // sort by name
  }, [contacts, searchTerm])

  return (
    <Dialog open={externalOpen} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="sm:max-w-md gap-0 !z-[100]"
      >
        <DialogHeader className="pb-4">
          {/* Mobile: Back Arrow */}
          <div className="sm:hidden flex items-center w-full">
            <DialogClose className="pr-2 pl-0 ml-0" tabIndex={-1}>
              <ArrowLeft className="h-6 w-6" />
              <span className="sr-only">Back</span>
            </DialogClose>
            <div className="flex-1 text-center">
              <DialogTitle>Contact List</DialogTitle>
            </div>
          </div>

          {/* Desktop Title - Hidden on mobile */}
          <DialogTitle className="hidden sm:block">Contact List</DialogTitle>
          {/* Desktop: Close X */}
          <div className="hidden sm:block absolute right-4 top-4">
            <DialogClose tabIndex={-1}>
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader>

        {/* Search Box */}
        <div className="p-4 bg-sahi-white">
          <div className="relative w-full bg-sahi-disable">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-sahi-primary-color" />
            <Input
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10"
            />
          </div>
        </div>


        {/* Contact List */}
        <div className="space-y-0 sm:max-h-[65vh] overflow-y-auto bg-sahi-disable">
          {filteredContacts.length === 0 ? (
            permissionDenied ? (
              <div className="flex flex-col items-center gap-3 text-center py-8 px-6 text-muted-foreground">
                <Settings className="h-6 w-6" />
                <div>
                  Contacts permission is not granted. Please allow contacts access for this app from your device Settings.
                </div>
                {onRequestPermission && (
                  <Button variant="outline" size="sm" onClick={onRequestPermission}>
                    Grant Permission
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No contacts found
              </div>
            )
          ) : (
            filteredContacts.map((contact, index) => (
              <div
                key={index}
                className="flex items-center px-4 py-3 bg-sahi-white hover:bg-white/50 cursor-pointer border-[0.5px] border-gray-100"
                onClick={() => onContactClick && onContactClick(contact)}
              >
                {/* Avatar */}
                <Avatar className="h-10 w-10 mr-3">
                  <AvatarFallback className="bg-sahi-blue-light text-sahi-blue text-sm font-medium">
                    {getInitials(contact.name)}
                  </AvatarFallback>
                </Avatar>

                {/* Contact Info */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground">
                    {contact.name}
                  </div>
                  {/* <div className="text-xs text-gray-500 truncate">
                    {contact.org}
                  </div> */}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
